// src/utils/processUtils.ts

import { ProcessItem, ParticipantWithResponsibility } from "@/types";

/**
 * Kontrollerar om det finns data för ett specifikt alternativ
 */
export const hasAlternativeData = (
  item: ProcessItem,
  alternative: 1 | 2
): boolean => {
  if (!item) return false;
  
  try {
    if (alternative === 1) {
      // Kontrollera om Deltagareochansvar1 finns och har innehåll
      return !!item.Deltagareochansvar1 && item.Deltagareochansvar1.trim() !== "";
    } else {
      // Kontrollera om Deltagareochansvar2 finns och har innehåll
      return !!item.Deltagareochansvar2 && item.Deltagareochansvar2.trim() !== "";
    }
  } catch (error) {
    console.error(`Fel vid kontroll av alternativ ${alternative}:`, error);
    return false;
  }
};

/**
 * Hämtar deltagare med ansvar för ett specifikt alternativ
 */
export const getParticipantsForAlternative = (
  item: ProcessItem, 
  alternative: 1 | 2
): ParticipantWithResponsibility[] => {
  if (!item) return [];
  
  try {
    let participantString = "";
    
    if (alternative === 1) {
      // Använd deltagaralternativ 1
      participantString = item.Deltagareochansvar1 || "";
    } else {
      // Använd deltagaralternativ 2
      participantString = item.Deltagareochansvar2 || "";
    }
    
    // Hämta alla deltagare med deras respektive ansvar
    const allParticipants = parseParticipantsWithResponsibilities(participantString);
    
    // Gruppera ansvar per roll
    return consolidateParticipantResponsibilities(allParticipants);
  } catch (error) {
    console.error(`Fel vid hämtning av deltagare för alternativ ${alternative}:`, error);
    return [];
  }
};

/**
 * Tolkar deltagarsträngen med format "Roll: Ansvar", avgränsat med semikolon (;)
 */
export const parseParticipantsWithResponsibilities = (
  participantString?: string
): ParticipantWithResponsibility[] => {
  if (!participantString) return [];
  
  try {
    // Dela upp strängen vid semikolon (;) i stället för kommatecken
    return participantString
      .split(";")
      .map(part => part.trim())
      .filter(Boolean)
      .map(part => {
        // Dela varje del vid första kolon
        const colonIndex = part.indexOf(":");
        if (colonIndex === -1) {
          // Om det inte finns något kolon, använd hela strängen som roll
          return { role: part, responsibility: "" };
        }
        
        // Extrahera roll och ansvar
        const role = part.substring(0, colonIndex).trim();
        const responsibility = part.substring(colonIndex + 1).trim();
        
        return { role, responsibility };
      });
  } catch (error) {
    console.error("Fel vid analys av deltagarsträngen:", error);
    return [];
  }
};

/**
 * Konsoliderar ansvar för samma roll
 */
export const consolidateParticipantResponsibilities = (
  participants: ParticipantWithResponsibility[]
): ParticipantWithResponsibility[] => {
  if (!participants || participants.length === 0) return [];
  
  try {
    const roleMap = new Map<string, string[]>();
    
    participants.forEach(({ role, responsibility }) => {
      if (!roleMap.has(role)) {
        roleMap.set(role, []);
      }
      if (responsibility && responsibility.trim() !== "") {
        roleMap.get(role)!.push(responsibility.trim());
      }
    });
    
    return Array.from(roleMap.entries()).map(([role, responsibilities]) => {
      const formatted = responsibilities.length > 1
        ? "• " + responsibilities.join("\n• ")
        : (responsibilities[0] || "");
      
      return { role, responsibility: formatted };
    });
  } catch (error) {
    console.error("Fel vid konsolidering av deltagaransvar:", error);
    return participants; // Returnera ursprungliga listan vid fel
  }
};

/**
 * Extraherar unika deltagare/roller från båda alternativ
 */
export const getUniqueParticipants = (item: ProcessItem): string[] => {
  if (!item) return [];
  
  try {
    const alt1 = parseParticipantsWithResponsibilities(item.Deltagareochansvar1);
    const alt2 = parseParticipantsWithResponsibilities(item.Deltagareochansvar2);
    const all = [...alt1, ...alt2].map(p => p.role);
    return [...new Set(all)];
  } catch (error) {
    console.error("Fel vid extrahering av unika deltagare:", error);
    return [];
  }
};

/**
 * Parse document links from a document string
 */
export const parseDocumentLinks = (docString: string) => {
  if (!docString) return [];
  return docString
    .split(";")
    .map(link => link.trim())
    .filter(Boolean)
    .map(link => {
      const regex = /(.*)\((.*)\)/;
      const match = link.match(regex);
      if (match) {
        return {
          title: match[1].trim(),
          url: match[2].trim(),
        };
      } else {
        return {
          title: link,
          url: "",
        };
      }
    });
};

/**
 * Parse participants (backward compatibility) - semikolon-baserat
 */
export const parseParticipants = (participantsStr: string) => {
  if (!participantsStr) return [];
  return participantsStr
    .split(";")
    .map(part => part.trim())
    .filter(Boolean);
};

/**
 * processHierarchicalNumbers: Sätter item.hierarchicalNumber = "1.2.3" osv.
 *
 * Nu uppdaterad så att varje Del av bolag (item.Delavbolag) räknas om från 1
 * med egna "mainIndexMap" och "subIndexMap". Därmed kan "Fastighet" och "Service"
 * båda börja på 1,2,3...
 */
export const processHierarchicalNumbers = (data: ProcessItem[]): ProcessItem[] => {
  // Håller koll på index per Del av bolag (ex "Fastighet", "Service", ...)
  const delavbolagMap = new Map<string, {
    mainIndexMap: Map<string, number>;
    subIndexMap: Map<string, Map<string, number>>;
    momentCountMap: Map<string, number>;
    mainIndexCounter: number;
  }>();

  data.forEach(item => {
    const delav = item.Delavbolag || "_IngenDelavbolag_";

    // Finns redan i map?
    if (!delavbolagMap.has(delav)) {
      delavbolagMap.set(delav, {
        mainIndexMap: new Map<string, number>(),
        subIndexMap: new Map<string, Map<string, number>>(),
        momentCountMap: new Map<string, number>(),
        mainIndexCounter: 0
      });
    }

    // Hämta info för detta Delavbolag
    const info = delavbolagMap.get(delav)!;
    const { mainIndexMap, subIndexMap, momentCountMap } = info;

    // Kolla om vi redan har index för just denna Title (Huvudprocess)
    if (!mainIndexMap.has(item.Title)) {
      info.mainIndexCounter++;
      mainIndexMap.set(item.Title, info.mainIndexCounter);
      subIndexMap.set(item.Title, new Map());
    }

    const currentMainIndex = mainIndexMap.get(item.Title)!;
    const localSubMap = subIndexMap.get(item.Title)!;

    // Kolla delprocess (field_0)
    if (!localSubMap.has(item.field_0)) {
      localSubMap.set(item.field_0, localSubMap.size + 1);
    }
    const currentSubIndex = localSubMap.get(item.field_0)!;

    // Öka momenträkningen
    const key = `${delav}||${item.Title}||${item.field_0}`;
    const prevCount = momentCountMap.get(key) || 0;
    momentCountMap.set(key, prevCount + 1);
    const currentMomentIndex = prevCount + 1;

    // Ex: "1.2.3"
    item.hierarchicalNumber = `${currentMainIndex}.${currentSubIndex}.${currentMomentIndex}`;
  });

  return data;
};
